import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Flower2, Clock, Users, CalendarDays, Image, Phone, ArrowRight, Sparkles, MapPin, Bell, X } from "lucide-react";
import { useFetch } from "../lib/useData";
import { useSettings } from "../context/SettingsContext";
import { Section } from "../components/public/PublicLayout";
import { Mandala } from "../components/Mandala";

const HERO_IMG = "/images/a3.jpeg";
function QuickAction({ to, icon: Icon, label, testid }) {
  return (
    <Link to={to} data-testid={testid}
      className="group flex flex-col items-center gap-3 p-5 rounded-2xl bg-white border border-amber-200/70 hover:border-orange-400 hover:shadow-md transition-all">
      <span className="w-14 h-14 rounded-full bg-amber-100 text-orange-700 grid place-items-center group-hover:bg-orange-600 group-hover:text-white transition-colors">
        <Icon className="w-6 h-6" />
      </span>
      <span className="text-sm font-semibold text-stone-700">{label}</span>
    </Link>
  );
}

function money(s, v) { return `${s?.currency_symbol || "₹"}${v}`; }

export default function Home() {
  const s = useSettings();
  const { data: schedule } = useFetch("/public/today-schedule");
  const { data: sevas } = useFetch("/public/sevas?featured=true");
  const { data: events } = useFetch("/public/events?upcoming=true");
  const { data: gallery } = useFetch("/public/gallery");
  const { data: announcements } = useFetch("/public/announcements");
  const { data: journey } = useFetch("/public/journey");


  const urgent = (announcements || []).find((a) => a.urgent);

  const [showNote, setShowNote] = useState(true);
  const latest = (announcements || []).slice().sort((a, b) => {
  const da = new Date(a.created_at || a.date || 0).getTime();
  const db = new Date(b.created_at || b.date || 0).getTime();
  return db - da;
})[0];


  return (
    <div>
      <AnimatePresence>
  {latest && showNote && (
    <motion.div
      key="announcement-popup"
      initial={{ opacity: 0, x: 80 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 80 }}
      transition={{ duration: 0.4 }}
      data-testid="announcement-popup"
      className="fixed z-50 top-1/2 -translate-y-1/2 right-4 w-[calc(100%-2rem)] sm:w-80 bg-white border border-amber-300 rounded-2xl shadow-2xl overflow-hidden"    >
      <div className="flex items-center gap-2 bg-gradient-to-r from-orange-600 to-amber-600 text-white px-4 py-2.5">
        <Bell className="w-4 h-4 animate-flicker" />
        <span className="text-sm font-bold">Latest Announcement</span>
        <button
          onClick={() => setShowNote(false)}
          aria-label="Close"
          data-testid="announcement-popup-close"
          className="ml-auto p-1 rounded-full hover:bg-white/25 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="px-4 py-4 text-sm text-stone-700 leading-relaxed">
        {latest.title}
      </div>
    </motion.div>
  )}
</AnimatePresence>
      {urgent && (
        <div className="bg-orange-700 text-white text-sm font-medium px-4 py-2.5 text-center flex items-center justify-center gap-2" data-testid="urgent-banner">
          <Bell className="w-4 h-4 animate-flicker" /> {urgent.title}
        </div>
      )}

      {/* HERO */}
      <section className="relative min-h-[88vh] flex items-center overflow-hidden mandala-bg">
        <img src={HERO_IMG} alt="Temple at dusk" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#2a0f0a]/90 via-[#2a0f0a]/55 to-[#2a0f0a]/20" />
        <Mandala className="absolute right-0 top-10 w-[520px] h-[520px] text-amber-300/15 hidden lg:block" />
        <div className="relative max-w-7xl mx-auto px-6 py-24 w-full">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-2xl">
            <img src={s.logo_url} alt="logo" className="h-20 w-20 rounded-full ring-4 ring-amber-400/40 mb-6" />
            <p className="font-deva text-amber-300 text-lg mb-3">॥ श्री हनुमते नमः ॥</p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-tight text-shadow-warm">
              {s.temple_name || "Sri Mehandipur Balaji Mandir"}
            </h1>
            <p className="text-amber-100/90 text-lg mt-5 max-w-xl leading-relaxed">
              A trusted digital home for devotees — darshan, seva, festivals and the blessings of Shri Balaji, in {s.temple_tagline || "Rajarajeshwari Nagar, Bengaluru"}.
            </p>
            <div className="flex flex-wrap gap-4 mt-8">
              <Link to="/seva" data-testid="hero-book-seva" className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white font-bold px-7 py-4 rounded-full shadow-lg transition-colors">
                <Flower2 className="w-5 h-5" /> Book a Seva
              </Link>
              <Link to="/worship/timings" data-testid="hero-timings" className="inline-flex items-center gap-2 bg-white/15 hover:bg-white/25 text-white font-bold px-7 py-4 rounded-full backdrop-blur border border-white/30 transition-colors">
                <Clock className="w-5 h-5" /> Temple Timings
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* TODAY'S SCHEDULE */}
      <Section className="!py-12">
        <div className="rounded-3xl bg-gradient-to-br from-orange-600 to-amber-600 p-8 md:p-10 text-white relative overflow-hidden">
          <Mandala className="absolute -right-10 -bottom-16 w-72 h-72 text-white/10" />
          <div className="relative">
            <div className="flex items-center gap-2 mb-6">
              <Sparkles className="w-5 h-5" />
              <h2 className="font-display text-2xl md:text-3xl font-bold">Today&apos;s Temple Schedule</h2>
              {schedule && <span className="ml-auto text-sm bg-white/20 rounded-full px-3 py-1">{schedule.weekday}, {schedule.date}</span>}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="today-schedule">
              {[["Morning", schedule?.morning], ["Evening", schedule?.evening], ["Morning Aarti", schedule?.morning_aarti], ["Evening Aarti", schedule?.evening_aarti]].map(([k, v]) => (
                <div key={k} className="bg-white/15 backdrop-blur rounded-2xl p-5">
                  <div className="text-amber-100 text-xs uppercase tracking-widest font-semibold">{k}</div>
                  <div className="text-lg font-bold mt-1">{v || "—"}</div>
                </div>
              ))}
            </div>
            {schedule?.special_activity && (
              <p className="mt-5 text-amber-50/90"><strong>Today:</strong> {schedule.special_activity}</p>
            )}
            <p className="mt-2 text-sm text-amber-100/80">{schedule?.darshan_note}</p>
          </div>
        </div>
      </Section>

      {/* QUICK ACTIONS */}
      <Section className="!pt-4">
        <div className="grid grid-cols-3 md:grid-cols-6 gap-4" data-testid="quick-actions">
          <QuickAction to="/seva" icon={Flower2} label="Book Seva" testid="qa-seva" />
          <QuickAction to="/worship/timings" icon={Clock} label="Timings" testid="qa-timings" />
          <QuickAction to="/worship/deities" icon={Users} label="Deities" testid="qa-deities" />
          <QuickAction to="/happenings/events" icon={CalendarDays} label="Events" testid="qa-events" />
          <QuickAction to="/community/gallery" icon={Image} label="Gallery" testid="qa-gallery" />
          <QuickAction to="/help/contact" icon={Phone} label="Contact" testid="qa-contact" />
        </div>
      </Section>

      {/* INTRO + HISTORY */}
      <Section className="!py-10 grid md:grid-cols-2 gap-10 items-center">
        <div className="relative">
          <img src="https://images.unsplash.com/photo-1665003725647-3ae0f01140b1?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000" alt="Temple architecture" className="rounded-3xl shadow-xl w-full h-[380px] object-cover" />
          <div className="absolute inset-0 diya-glow rounded-3xl pointer-events-none" />
        </div>
        <div>
          <span className="text-sm text-amber-700 uppercase tracking-widest font-semibold">About the Temple</span>
          <h2 className="font-display text-3xl md:text-4xl font-bold text-stone-900 mt-2">A sacred presence, rooted in devotion</h2>
          <p className="text-stone-600 text-lg leading-relaxed mt-4">
            Sri Mehandipur Balaji Mandir carries the sacred spirit of Mehandipur Balaji to the heart of Bengaluru.
            Learn about our history, origin, and the journey that shaped this spiritual home.
          </p>
          <div className="flex flex-wrap gap-3 mt-6">
            <Link to="/about/history" className="text-orange-700 font-semibold inline-flex items-center gap-1 hover:gap-2 transition-all" data-testid="home-history-link">Temple History <ArrowRight className="w-4 h-4" /></Link>
            <Link to="/about/origin" className="text-orange-700 font-semibold inline-flex items-center gap-1 hover:gap-2 transition-all" data-testid="home-origin-link">Origin & Connection <ArrowRight className="w-4 h-4" /></Link>
            <Link to="/about/journey" className="text-orange-700 font-semibold inline-flex items-center gap-1 hover:gap-2 transition-all" data-testid="home-journey-link">Our Journey <ArrowRight className="w-4 h-4" /></Link>
          </div>
          {journey?.length > 0 && (
            <div className="flex gap-6 mt-8">
              {journey.slice(0, 3).map((j) => (
                <div key={j.id} className="text-center">
                  <div className="font-display text-2xl font-extrabold text-orange-700">{j.year}</div>
                  <div className="text-xs text-stone-500">{j.title}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Section>

      {/* FEATURED SEVA */}
      <div className="bg-amber-50/70">
        <Section>
          <div className="flex items-end justify-between mb-8">
            <div>
              <span className="text-sm text-amber-700 uppercase tracking-widest font-semibold">Seva & Pooja</span>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-stone-900 mt-2">Featured Sevas</h2>
            </div>
            <Link to="/seva" className="text-orange-700 font-semibold inline-flex items-center gap-1 hover:gap-2 transition-all" data-testid="home-all-seva">View all <ArrowRight className="w-4 h-4" /></Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="featured-sevas">
            {(sevas || []).slice(0, 3).map((seva) => (
              <div key={seva.id} className="group bg-white rounded-2xl overflow-hidden border border-amber-200/70 hover:shadow-lg transition-all flex flex-col">
                <div className="h-48 overflow-hidden">
                  <img src={seva.image} alt={seva.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-5 flex flex-col flex-1">
                  <span className="text-xs font-semibold text-amber-700">{seva.category_name}</span>
                  <h3 className="font-display text-xl font-bold text-stone-900 mt-1">{seva.name}</h3>
                  <p className="text-sm text-stone-500 mt-2 line-clamp-2 flex-1">{seva.short_description}</p>
                  <div className="flex items-center justify-between mt-4">
                    <span className="text-xl font-extrabold text-orange-700">{money(s, seva.price)}</span>
                    <Link to={`/seva/${seva.slug}`} className="bg-orange-600 hover:bg-orange-700 text-white text-sm font-bold px-4 py-2 rounded-full transition-colors" data-testid={`home-book-${seva.slug}`}>Book Now</Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Section>
      </div>

      {/* UPCOMING EVENTS */}
      <Section>
        <div className="flex items-end justify-between mb-8">
          <div>
            <span className="text-sm text-amber-700 uppercase tracking-widest font-semibold">Happenings</span>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-stone-900 mt-2">Upcoming Events & Festivals</h2>
          </div>
          <Link to="/happenings/events" className="text-orange-700 font-semibold inline-flex items-center gap-1 hover:gap-2 transition-all" data-testid="home-all-events">View all <ArrowRight className="w-4 h-4" /></Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="home-events">
          {(events || []).slice(0, 3).map((ev) => (
            <Link to={`/happenings/events/${ev.slug}`} key={ev.id} className="group relative rounded-2xl overflow-hidden h-64 block" data-testid={`home-event-${ev.slug}`}>
              <img src={ev.cover_image} alt={ev.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
              <div className="absolute bottom-0 p-5 text-white">
                <span className="text-xs bg-orange-600 rounded-full px-2 py-0.5 font-semibold">{ev.category}</span>
                <h3 className="font-display text-xl font-bold mt-2">{ev.title}</h3>
                <p className="text-sm text-amber-100/80 mt-1">{ev.start_date}</p>
              </div>
            </Link>
          ))}
        </div>
      </Section>

      {/* SOCIAL SERVICE */}
      <div className="relative bg-[#2a0f0a] text-white overflow-hidden">
        <img src="https://images.unsplash.com/photo-1594385366598-a97b77392443?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400" alt="Gaushala" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="relative max-w-7xl mx-auto px-6 py-20 text-center">
          <span className="text-sm text-amber-400 uppercase tracking-widest font-semibold">Seva to Society</span>
          <h2 className="font-display text-3xl md:text-4xl font-bold mt-2">Gau Seva & Social Service</h2>
          <p className="text-amber-100/80 max-w-2xl mx-auto mt-4 text-lg">The temple cares for indigenous cows through its Gaushala and supports community initiatives in the spirit of seva.</p>
          <Link to="/community/social-service" className="inline-flex items-center gap-2 mt-7 bg-orange-600 hover:bg-orange-700 text-white font-bold px-6 py-3.5 rounded-full transition-colors" data-testid="home-social-link">Learn about Gau Seva <ArrowRight className="w-4 h-4" /></Link>
        </div>
      </div>

      {/* GALLERY PREVIEW */}
      <Section>
        <div className="flex items-end justify-between mb-8">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-stone-900">Gallery</h2>
          <Link to="/community/gallery" className="text-orange-700 font-semibold inline-flex items-center gap-1 hover:gap-2 transition-all" data-testid="home-all-gallery">View all <ArrowRight className="w-4 h-4" /></Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="home-gallery">
          {(gallery || []).slice(0, 8).map((g) => (
            <div key={g.id} className="aspect-square rounded-xl overflow-hidden group">
              <img src={g.image} alt={g.alt_text} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
            </div>
          ))}
        </div>
      </Section>

      {/* LOCATION */}
      <div className="bg-amber-50/70">
        <Section className="grid md:grid-cols-2 gap-10 items-center">
          <div>
            <span className="text-sm text-amber-700 uppercase tracking-widest font-semibold">Visit Us</span>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-stone-900 mt-2">Location & Directions</h2>
            <p className="flex gap-2 text-stone-600 mt-4 text-lg"><MapPin className="w-5 h-5 text-orange-600 shrink-0 mt-1" /> {s.address}</p>
            <p className="flex gap-2 text-stone-600 mt-3"><Phone className="w-5 h-5 text-orange-600 shrink-0" /> {s.phone}</p>
            <div className="flex gap-3 mt-6">
              <a href={s.maps_directions_url} target="_blank" rel="noreferrer" className="bg-orange-600 hover:bg-orange-700 text-white font-bold px-5 py-3 rounded-full transition-colors" data-testid="home-directions">Get Directions</a>
              <Link to="/help/contact" className="border border-orange-300 text-orange-700 font-bold px-5 py-3 rounded-full hover:bg-amber-100 transition-colors" data-testid="home-contact">Contact</Link>
            </div>
          </div>
          <div className="rounded-3xl overflow-hidden shadow-lg h-80 border border-amber-200">
            <iframe title="map" src={s.maps_embed_url} className="w-full h-full" loading="lazy" />
          </div>
        </Section>
      </div>
    </div>
  );
}
